import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { isUserAdmin, supabase } from "../../../lib/supabase";

interface Mentor {
  uuid: string;
  user_uuid: string;
  name: string;
  email: string;
  specialization: string;
}

interface Mentee {
  uuid: string;
  user_uuid: string;
  name: string;
  email: string;
}

export function NewAssignment() {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [selectedMentor, setSelectedMentor] = useState<string>("");
  const [selectedMentee, setSelectedMentee] = useState<string>("");
  const [mentorSearch, setMentorSearch] = useState("");
  const [menteeSearch, setMenteeSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showMentorDropdown, setShowMentorDropdown] = useState(false);
  const [showMenteeDropdown, setShowMenteeDropdown] = useState(false);

  useEffect(() => {
    async function checkAdminStatus() {
      const adminStatus = await isUserAdmin();
      setIsAdmin(adminStatus);
      if (!adminStatus) {
        setError("You must be an admin to create assignments");
      }
    }
    checkAdminStatus();
  }, []);

  useEffect(() => {
    async function fetchUsers() {
      try {
        // Fetch mentors
        const { data: mentorData, error: mentorError } = await supabase.from(
          "mentor"
        ).select(`
            uuid,
            user_uuid,
            specialization,
            vincer_base_user (
              name,
              email
            )
          `);

        if (mentorError) throw mentorError;

        // Fetch mentees
        const { data: menteeData, error: menteeError } = await supabase.from(
          "mentee"
        ).select(`
            uuid,
            user_uuid,
            vincer_base_user (
              name,
              email
            )
          `);

        if (menteeError) throw menteeError;

        const formattedMentors = (
          mentorData as unknown[] as {
            uuid: string;
            user_uuid: string;
            specialization: string;
            vincer_base_user: { name: string; email: string };
          }[]
        ).map((mentor) => ({
          uuid: mentor.uuid,
          user_uuid: mentor.user_uuid,
          name: mentor.vincer_base_user.name,
          email: mentor.vincer_base_user.email,
          specialization: mentor.specialization,
        }));

        const formattedMentees = (
          menteeData as unknown[] as {
            uuid: string;
            user_uuid: string;
            vincer_base_user: { name: string; email: string };
          }[]
        ).map((mentee) => ({
          uuid: mentee.uuid,
          user_uuid: mentee.user_uuid,
          name: mentee.vincer_base_user.name,
          email: mentee.vincer_base_user.email,
        }));

        setMentors(formattedMentors);
        setMentees(formattedMentees);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users");
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, []);

  const filteredMentors = useMemo(() => {
    return mentors.filter(
      (mentor) =>
        mentor.name.toLowerCase().includes(mentorSearch.toLowerCase()) ||
        mentor.email.toLowerCase().includes(mentorSearch.toLowerCase()) ||
        mentor.specialization.toLowerCase().includes(mentorSearch.toLowerCase())
    );
  }, [mentors, mentorSearch]);

  const filteredMentees = useMemo(() => {
    return mentees.filter(
      (mentee) =>
        mentee.name.toLowerCase().includes(menteeSearch.toLowerCase()) ||
        mentee.email.toLowerCase().includes(menteeSearch.toLowerCase())
    );
  }, [mentees, menteeSearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) {
      setError("You must be an admin to create assignments");
      return;
    }

    if (!selectedMentor || !selectedMentee) {
      setError("Please select both a mentor and a mentee");
      return;
    }

    try {
      const { error: assignmentError } = await supabase
        .from("relationship_assignments")
        .insert({
          uuid: crypto.randomUUID(),
          mentor_uuid: selectedMentor,
          mentee_uuid: selectedMentee,
          start: new Date().toISOString(),
          end: null, // Can be updated later when the assignment ends
        });

      if (assignmentError) throw assignmentError;

      navigate("/coordinator/assignments");
    } catch (err) {
      console.error("Error creating assignment:", err);
      setError("Failed to create assignment");
    }
  };

  const getSelectedMentorName = () => {
    const mentor = mentors.find((m) => m.uuid === selectedMentor);
    return mentor ? `${mentor.name} (${mentor.email})` : "";
  };

  const getSelectedMenteeName = () => {
    const mentee = mentees.find((m) => m.uuid === selectedMentee);
    return mentee ? `${mentee.name} (${mentee.email})` : "";
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-b-2 border-amber-400"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold">Assign Mentor to Mentee</h1>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-6 rounded-lg bg-white p-6 shadow-sm"
      >
        <div className="relative">
          <label
            htmlFor="mentor"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Select Mentor
          </label>
          <div className="relative">
            <input
              type="text"
              value={selectedMentor ? getSelectedMentorName() : mentorSearch}
              onChange={(e) => {
                setMentorSearch(e.target.value);
                setSelectedMentor("");
                setShowMentorDropdown(true);
              }}
              onFocus={() => setShowMentorDropdown(true)}
              className="w-full rounded-lg border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
              placeholder="Search mentors..."
              disabled={!isAdmin}
            />
            <Search className="absolute right-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
          </div>
          {showMentorDropdown && mentorSearch && (
            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border bg-white shadow-lg">
              {filteredMentors.map((mentor) => (
                <button
                  key={mentor.uuid}
                  type="button"
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:outline-none"
                  onClick={() => {
                    setSelectedMentor(mentor.uuid);
                    setMentorSearch("");
                    setShowMentorDropdown(false);
                  }}
                >
                  <div className="font-medium">{mentor.name}</div>
                  <div className="text-sm text-gray-500">{mentor.email}</div>
                  <div className="text-sm text-gray-400">
                    {mentor.specialization}
                  </div>
                </button>
              ))}
              {filteredMentors.length === 0 && (
                <div className="px-4 py-2 text-gray-500">No mentors found</div>
              )}
            </div>
          )}
        </div>

        <div className="relative">
          <label
            htmlFor="mentee"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Select Mentee
          </label>
          <div className="relative">
            <input
              type="text"
              value={selectedMentee ? getSelectedMenteeName() : menteeSearch}
              onChange={(e) => {
                setMenteeSearch(e.target.value);
                setSelectedMentee("");
                setShowMenteeDropdown(true);
              }}
              onFocus={() => setShowMenteeDropdown(true)}
              className="w-full rounded-lg border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
              placeholder="Search mentees..."
              disabled={!isAdmin}
            />
            <Search className="absolute right-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
          </div>
          {showMenteeDropdown && menteeSearch && (
            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border bg-white shadow-lg">
              {filteredMentees.map((mentee) => (
                <button
                  key={mentee.uuid}
                  type="button"
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:outline-none"
                  onClick={() => {
                    setSelectedMentee(mentee.uuid);
                    setMenteeSearch("");
                    setShowMenteeDropdown(false);
                  }}
                >
                  <div className="font-medium">{mentee.name}</div>
                  <div className="text-sm text-gray-500">{mentee.email}</div>
                </button>
              ))}
              {filteredMentees.length === 0 && (
                <div className="px-4 py-2 text-gray-500">No mentees found</div>
              )}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !isAdmin || !selectedMentor || !selectedMentee}
          className="flex w-full justify-center rounded-md border border-transparent bg-[#4CAF50] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#45a049] focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Create Assignment
        </button>
      </form>
    </div>
  );
}
